import {
  FieldWrapper,
  Icons,
  InputErrorMessage,
  Label,
  NumberInput,
  Switch,
  Tooltip,
} from '@commenda/kakashi';
import type { SharesComputation, SharesConfig } from './compute';
import type { OwnershipFieldSettings } from './types';
import { useRevealErrors } from './useRevealErrors';

interface SharesSectionsProps {
  config: SharesConfig;
  onPatch: (patch: Partial<SharesConfig>) => void;
  computation: SharesComputation;
  // ISO-4217 code shown as the money prefix (e.g. "EUR", "USD").
  currency: string;
  // Author-configured, jurisdiction-driven: min share capital + helper copy.
  jurisdiction?: { minShareCapital?: number; helperText?: string };
  // Author toggle: show the Share Capital input (and derive price). Shares only.
  shareCapitalEnabled: boolean;
  fieldSettings: OwnershipFieldSettings;
  // True when at least one owner is staged — authorized shares is then required.
  hasOwners: boolean;
  required: boolean;
  showErrors: boolean;
  authorizedSharesDescription: string;
}

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <h3 className="text-base font-bold text-text-primary">{title}</h3>
      {description ? (
        <p className="text-sm text-text-secondary">{description}</p>
      ) : null}
    </div>
    {children}
  </div>
);

const LabelWithInfo = ({
  label,
  info,
  required,
}: {
  label: string;
  info: string;
  required?: boolean;
}) => (
  <div className="flex h-5 items-center gap-1.5">
    <Label required={required} className="!font-normal text-text-tertiary">
      {label}
    </Label>
    <Tooltip content={info}>
      <span className="inline-flex cursor-help text-text-tertiary">
        <Icons.InfoCircle className="size-3.5" />
      </span>
    </Tooltip>
  </div>
);

const MoneyPrefix = (code: string) => () => (
  <span className="flex w-full items-center justify-start txt-small-regular text-text-tertiary">
    {code}
  </span>
);
const PercentSuffix = () => (
  <span className="flex w-full items-center justify-center txt-small-regular text-text-tertiary">
    %
  </span>
);

export const SharesSections = ({
  config,
  onPatch,
  computation,
  currency,
  jurisdiction,
  shareCapitalEnabled,
  fieldSettings,
  hasOwners,
  required,
  showErrors,
  authorizedSharesDescription,
}: SharesSectionsProps) => {
  const authorized = fieldSettings.authorizedShares;
  const esop = fieldSettings.esopPercent;
  const issued = fieldSettings.issuedShares;
  const price = fieldSettings.pricePerShare;
  const minCapital = jurisdiction?.minShareCapital;
  // Each field reveals its error on blur; a submit/next attempt (`showErrors`)
  // reveals them all.
  const { touch, reveal } = useRevealErrors(showErrors);
  const needFill = required || hasOwners;
  const capitalField = shareCapitalEnabled
    ? 'totalShareCapital'
    : 'pricePerShare';

  const authorizedMissing =
    reveal('authorizedShares') &&
    needFill &&
    !(config.authorizedShares && config.authorizedShares >= 1);
  const issuedMissing =
    reveal('issuedShares') &&
    needFill &&
    !(config.issuedShares && config.issuedShares >= 1);
  const capitalMissing =
    reveal('totalShareCapital') &&
    needFill &&
    shareCapitalEnabled &&
    !(config.totalShareCapital && config.totalShareCapital >= 1);
  const priceMissing =
    reveal('pricePerShare') &&
    needFill &&
    !shareCapitalEnabled &&
    !(computation.pricePerShare > 0);
  const esopMissing =
    reveal('esopPercent') &&
    esop.visible &&
    !!config.esopEnabled &&
    !(config.esopPercent && config.esopPercent > 0);
  const belowMinCapital =
    minCapital != null &&
    computation.effectiveShareCapital > 0 &&
    computation.effectiveShareCapital < minCapital &&
    reveal(capitalField);
  const exceedsMaxIssuable =
    computation.exceedsMaxIssuable && reveal('issuedShares');

  return (
    <div className="flex flex-col gap-8">
      {shareCapitalEnabled ? (
        <Section
          title="Share Capital"
          description="Set your company's total share capital. This is the monetary value committed at formation, divided into shares or quotas held by your owners."
        >
          <FieldWrapper>
            <Label
              required={required}
              className="!font-normal text-text-tertiary"
            >
              Total share capital
            </Label>
            <NumberInput
              formatWithCommas
              className="pl-12"
              placeholder="25,000"
              value={config.totalShareCapital ?? undefined}
              leftEl={MoneyPrefix(currency)}
              error={belowMinCapital || capitalMissing ? ' ' : undefined}
              showErrorMessage={false}
              onBlur={() => touch('totalShareCapital')}
              onChange={(n) => onPatch({ totalShareCapital: n ?? undefined })}
            />
            {capitalMissing ? (
              <InputErrorMessage message="Total share capital is required." />
            ) : belowMinCapital ? (
              <InputErrorMessage
                message={`Minimum share capital is ${currency} ${(minCapital as number).toLocaleString('en-US')}.`}
              />
            ) : jurisdiction?.helperText ? (
              <span className="txt-extra-small-regular text-text-tertiary">
                {jurisdiction.helperText}
              </span>
            ) : null}
          </FieldWrapper>
        </Section>
      ) : null}

      {authorized.visible ? (
        <Section
          title="Authorized Shares"
          description={authorizedSharesDescription}
        >
          <FieldWrapper>
            <Label
              required={required}
              className="!font-normal text-text-tertiary"
            >
              Authorized shares
            </Label>
            <NumberInput
              formatWithCommas
              placeholder="10,000,000"
              value={config.authorizedShares ?? undefined}
              error={authorizedMissing ? ' ' : undefined}
              showErrorMessage={false}
              disabled={!authorized.editable}
              onBlur={() => touch('authorizedShares')}
              onChange={(n) => onPatch({ authorizedShares: n ?? undefined })}
            />
            {authorizedMissing ? (
              <InputErrorMessage message="Authorized shares is required." />
            ) : null}
          </FieldWrapper>
        </Section>
      ) : null}

      {esop.visible ? (
        <Section
          title="Employee Stock Option Pool (ESOP)"
          description="ESOP pool shares aren't reflected in the ownership summary below because they remain unissued until granted as options and exercised by their holders. You'll need to set up an employee stock option plan separately before granting options."
        >
          <div className="flex items-start gap-3">
            <Switch
              checked={!!config.esopEnabled}
              disabled={!esop.editable}
              onCheckedChange={(checked) => onPatch({ esopEnabled: checked })}
            />
            {/* Label + (when enabled) the pool-size input share the same left edge,
              indented past the switch per the design. */}
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex flex-col">
                <Label className="text-text-primary">
                  Create an employee stock option pool
                </Label>
                <span className="text-xs font-normal text-text-tertiary">
                  Equity reserved in the ESOP remains unissued until granted.
                </span>
              </div>
              {config.esopEnabled ? (
                <FieldWrapper _className="w-1/2">
                  <LabelWithInfo
                    required
                    label="Equity pool size %"
                    info="Share of authorized shares reserved for the employee option pool. Reserved, not issued — it never appears as owned in the summary."
                  />
                  <NumberInput
                    formatWithCommas
                    className="pr-9"
                    placeholder="20.00"
                    value={config.esopPercent ?? undefined}
                    rightEl={PercentSuffix}
                    error={esopMissing ? ' ' : undefined}
                    showErrorMessage={false}
                    disabled={!esop.editable}
                    onBlur={() => touch('esopPercent')}
                    onChange={(n) => onPatch({ esopPercent: n ?? undefined })}
                  />
                  {esopMissing ? (
                    <InputErrorMessage message="Equity pool size is required." />
                  ) : null}
                </FieldWrapper>
              ) : null}
            </div>
          </div>
        </Section>
      ) : null}

      {/* Issued shares + price are always shown; only their editability is toggleable. */}
      <Section
        title="Issued Shares"
        description="Set the total number of issued shares and the price per share."
      >
        <div className="grid grid-cols-2 gap-4">
          <FieldWrapper>
            <Label
              required={required}
              className="!font-normal text-text-tertiary flex h-5 items-center"
            >
              Issued shares
            </Label>
            <NumberInput
              formatWithCommas
              placeholder="6,000,000"
              value={config.issuedShares ?? undefined}
              error={issuedMissing || exceedsMaxIssuable ? ' ' : undefined}
              showErrorMessage={false}
              disabled={!issued.editable}
              onBlur={() => touch('issuedShares')}
              onChange={(n) => onPatch({ issuedShares: n ?? undefined })}
            />
            {issuedMissing ? (
              <InputErrorMessage message="Issued shares is required." />
            ) : exceedsMaxIssuable ? (
              <InputErrorMessage
                message={`Maximum issuable shares under this structure is ${computation.maxIssuable.toLocaleString('en-US')}.`}
              />
            ) : (
              <span className="txt-extra-small-regular text-text-tertiary">
                Please note that the amount of issued shares and price per share
                will affect the initial capital contribution amount.
              </span>
            )}
          </FieldWrapper>
          <FieldWrapper>
            <LabelWithInfo
              required={required && !shareCapitalEnabled}
              label="Price per share"
              info="The initial offering price per share. When share capital is enabled, it's derived as share capital ÷ authorized shares."
            />
            <NumberInput
              formatWithCommas
              className="pl-12"
              placeholder="0.00"
              value={
                computation.priceIsDerived
                  ? computation.pricePerShare
                  : (config.pricePerShare ?? undefined)
              }
              leftEl={MoneyPrefix(currency)}
              error={
                priceMissing || (!shareCapitalEnabled && belowMinCapital)
                  ? ' '
                  : undefined
              }
              showErrorMessage={false}
              disabled={computation.priceIsDerived || !price.editable}
              onBlur={() => touch('pricePerShare')}
              onChange={(n) => onPatch({ pricePerShare: n ?? undefined })}
            />
            {priceMissing ? (
              <InputErrorMessage message="Price per share is required." />
            ) : null}
          </FieldWrapper>
        </div>
        {!shareCapitalEnabled && belowMinCapital ? (
          <InputErrorMessage
            message={`Minimum share capital is ${currency} ${(minCapital as number).toLocaleString('en-US')}.`}
          />
        ) : null}
      </Section>
    </div>
  );
};
